<template>
  <LoadingSpinner v-if="projectsLoading || vendorsLoading" />

  <div v-else class="create-purchase-order">
    <PageHeader title="Add Purchase Order" />
    <div class="mx-auto max-w-7xl">
      <PurchaseOrderForm
        :project-id="projectId"
        :projects="projects"
        :vendors="vendors"
        :save-function="createPurchaseOrder"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { PurchaseOrder } from '@/models/purchaseOrder'
import PurchaseOrderForm from '../components/PurchaseOrderForm.vue'
import PageHeader from '@/components/app/AppPageHeader.vue'
import { useHead } from '@unhead/vue'
import PorjectService from '@/services/ProjectService'
import { useRouter, useRoute } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import BudgetService from '@/services/BudgetService'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { PROJECT_STATUS } from '@/models/project'
import VendorService from '@/services/VendorService'

useHead({ title: 'Add Purchase Order' })


const $router = useRouter()
const { notify } = useNotification()
const projectId = useRoute().query.projectId as string || ''
const queryClient = useQueryClient()

const { data: projects, isLoading:projectsLoading } = useQuery({
  queryKey: ['projects'],
  queryFn: () =>  new PorjectService().find_by('status', '==', PROJECT_STATUS.IN_PROGRESS).then((projects) => projects.map((project) => ({ value: project.id, label: project.name, status: project.status, address: project.address })))
})

const { data: vendors, isLoading:vendorsLoading } = useQuery({
  queryKey: ['vendors'],
  queryFn: () => new VendorService().all().then((vendors) => vendors.map((vendor) => ({ value: vendor.id, label: vendor.name })))
})

const { mutateAsync: createPurchaseOrder } = useMutation({
  mutationFn: (purchaseOrder: PurchaseOrder) => new BudgetService().addPurchaseOrder(purchaseOrder.budgetId, purchaseOrder),
  onSuccess: (createdPO: PurchaseOrder) => {
    notify({
      type: 'success',
      title: 'Purchase Order Created',
      text: 'The purchase order has been created successfully'
    })

    queryClient.setQueryData(['purchaseOrder', createdPO.id], createdPO)
    queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] })
    $router.push({ name: 'ShowPurchaseOrder', params: { id: createdPO.id }, hash: '#showConfirmation' })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error Creating Purchase Order',
      text: error.message || 'An error occurred while creating the purchase order'
    })
  }
})

</script>
